import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { StarIcon, CheckCircleIcon, ShuffleIcon, HelpCircleIcon } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
interface Word {
  id: number;
  en: string;
  ar: string;
}
interface MatchingGameProps {
  words?: Word[];
}
const SAMPLE_WORDS: Word[] = [{
  id: 1,
  en: 'Hello',
  ar: 'مرحباً'
}, {
  id: 2,
  en: 'Thank you',
  ar: 'شكراً'
}, {
  id: 3,
  en: 'Good morning',
  ar: 'صباح الخير'
}, {
  id: 4,
  en: 'Welcome',
  ar: 'أهلاً وسهلاً'
}, {
  id: 5,
  en: 'Goodbye',
  ar: 'مع السلامة'
}];
export function WordMatchingGame({
  words = SAMPLE_WORDS
}: MatchingGameProps) {
  const {
    isArabic
  } = useLanguage();
  const [selectedWord, setSelectedWord] = useState<number | null>(null);
  const [selectedTranslation, setSelectedTranslation] = useState<number | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<Set<number>>(new Set());
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [shuffledWords, setShuffledWords] = useState<Word[]>([]);
  const [shuffledTranslations, setShuffledTranslations] = useState<Word[]>([]);
  const shuffleArrays = () => {
    setShuffledWords([...words].sort(() => Math.random() - 0.5));
    setShuffledTranslations([...words].sort(() => Math.random() - 0.5));
  };
  useEffect(() => {
    shuffleArrays();
  }, [words]);
  const handleWordClick = (id: number) => {
    if (matchedPairs.has(id)) return;
    setSelectedWord(id);
    checkMatch(id, selectedTranslation);
  };
  const handleTranslationClick = (id: number) => {
    if (matchedPairs.has(id)) return;
    setSelectedTranslation(id);
    checkMatch(selectedWord, id);
  };
  const checkMatch = (wordId: number | null, translationId: number | null) => {
    if (wordId === null || translationId === null) return;
    if (wordId === translationId) {
      setMatchedPairs(new Set([...matchedPairs, wordId]));
      setScore(score + 100);
      setStreak(streak + 1);
      setSelectedWord(null);
      setSelectedTranslation(null);
    } else {
      setTimeout(() => {
        setSelectedWord(null);
        setSelectedTranslation(null);
        setStreak(0);
      }, 1000);
    }
  };
  const resetGame = () => {
    setMatchedPairs(new Set());
    setScore(0);
    setStreak(0);
    setSelectedWord(null);
    setSelectedTranslation(null);
    shuffleArrays();
  };
  const isGameComplete = matchedPairs.size === words.length;
  return <div className="max-w-4xl mx-auto p-6" data-id="element-1686">
      <div className="text-center mb-8" data-id="element-1687">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-1688">
          {isArabic ? 'مطابقة الكلمات' : 'Word Matching'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400" data-id="element-1689">
          {isArabic ? 'اربط كل كلمة مع ترجمتها الصحيحة' : 'Match each word with its correct translation'}
        </p>
      </div>
      <div className="flex justify-between items-center mb-8" data-id="element-1690">
        <div className="flex items-center gap-4" data-id="element-1691">
          <div className="text-gray-600 dark:text-gray-400" data-id="element-1692">
            {isArabic ? 'النتيجة:' : 'Score:'} {score}
          </div>
          {streak > 1 && <Tooltip content={isArabic ? `سلسلة من ${streak} إجابات صحيحة` : `Streak of ${streak} correct matches`} data-id="element-1693">
              <div className="flex items-center gap-1 text-yellow-500" data-id="element-1694">
                <StarIcon className="w-5 h-5" data-id="element-1695" />
                <span data-id="element-1696">{streak}</span>
              </div>
            </Tooltip>}
        </div>
        <div className="flex gap-2" data-id="element-1697">
          <Tooltip content={isArabic ? 'خلط الكلمات' : 'Shuffle words'} data-id="element-1698">
            <button onClick={shuffleArrays} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-1699">
              <ShuffleIcon className="w-5 h-5" data-id="element-1700" />
            </button>
          </Tooltip>
          <Tooltip content={isArabic ? 'إعادة اللعبة' : 'Reset game'} data-id="element-1701">
            <button onClick={resetGame} className="p-2 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors" data-id="element-1702">
              <HelpCircleIcon className="w-5 h-5" data-id="element-1703" />
            </button>
          </Tooltip>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1704">
        <div className="space-y-4" data-id="element-1705">
          {shuffledWords.map(word => <button key={word.id} onClick={() => handleWordClick(word.id)} disabled={matchedPairs.has(word.id)} className={`
                w-full p-4 rounded-lg text-left transition-all
                ${matchedPairs.has(word.id) ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : selectedWord === word.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
                ${matchedPairs.has(word.id) ? 'cursor-default' : 'cursor-pointer'}
              `} data-id="element-1706">
              <span className="font-medium" data-id="element-1707">{word.en}</span>
              {matchedPairs.has(word.id) && <CheckCircleIcon className="w-5 h-5 float-right" data-id="element-1708" />}
            </button>)}
        </div>
        <div className="space-y-4" dir="rtl" data-id="element-1709">
          {shuffledTranslations.map(word => <button key={word.id} onClick={() => handleTranslationClick(word.id)} disabled={matchedPairs.has(word.id)} className={`
                w-full p-4 rounded-lg text-right transition-all
                ${matchedPairs.has(word.id) ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : selectedTranslation === word.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'}
                ${matchedPairs.has(word.id) ? 'cursor-default' : 'cursor-pointer'}
              `} data-id="element-1710">
              <span className="font-medium" data-id="element-1711">{word.ar}</span>
              {matchedPairs.has(word.id) && <CheckCircleIcon className="w-5 h-5 float-left" data-id="element-1712" />}
            </button>)}
        </div>
      </div>
      {isGameComplete && <div className="mt-8 text-center" data-id="element-1713">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full" data-id="element-1714">
            <CheckCircleIcon className="w-5 h-5" data-id="element-1715" />
            <span data-id="element-1716">
              {isArabic ? 'أحسنت! لقد أكملت جميع المطابقات' : 'Great job! You completed all matches'}
            </span>
          </div>
        </div>}
    </div>;
}